import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { ShoppingBag, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: "all", label: "Alle" },
    { id: "cleaning", label: "Reinigung" },
    { id: "protection", label: "Schutz" },
    { id: "accessories", label: "Zubehör" },
  ];

  const filteredProducts = selectedCategory === "all"
    ? products
    : products.filter((p) => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" data-testid="products-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 font-['Chivo']">
            Unsere <span className="text-cyan-400">Produkte</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Hochwertige Pflegeprodukte für Ihr Fahrzeug – die gleichen Produkte, 
            die wir bei unseren professionellen Aufbereitungen verwenden.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(category.id)}
              className={
                selectedCategory === category.id
                  ? "bg-cyan-500 text-black hover:bg-cyan-400"
                  : "border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600"
              }
              data-testid={`category-${category.id}`}
            >
              {category.label}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="service-card bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden hover:border-cyan-500/50"
              data-testid={`product-${product.id}`}
            >
              <div className="img-zoom h-48 bg-zinc-800">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400";
                  }}
                />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white line-clamp-2">
                    {product.name}
                  </h3>
                </div>
                <p className="text-xs text-zinc-500 mb-4 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-white price-tag">
                    {product.price.toFixed(2)}€
                  </span>
                  {product.in_stock ? (
                    <div className="flex items-center gap-1 text-xs text-green-400">
                      <Check className="h-3 w-3" />
                      Auf Lager
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-500">Ausverkauft</span>
                  )}
                </div>
                <Button
                  className="w-full mt-4 bg-zinc-800 hover:bg-zinc-700 text-white"
                  disabled={!product.in_stock}
                  data-testid={`buy-product-${product.id}`}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Im Shop erhältlich
                </Button>
              </div>
            </div>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-500">Keine Produkte in dieser Kategorie gefunden.</p>
          </div>
        )}

        {/* Info Banner */}
        <div className="mt-12 p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm">
          <div className="flex items-center gap-4">
            <ShoppingBag className="h-8 w-8 text-cyan-400 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">
                Produkte vor Ort erhältlich
              </h3>
              <p className="text-sm text-zinc-500">
                Alle Produkte können Sie direkt bei uns im Shop erwerben. 
                Sprechen Sie uns bei Ihrem nächsten Termin an.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
